import jsPDF from 'jspdf';
import { PlatformResult, Business } from '../types/api';

interface ReportData {
  businessInfo: Business;
  formData: Record<string, string>;
  platformResults: PlatformResult[];
  overallScore: number;
  isComplete: boolean;
}

// Helper function to replace template variables
const replaceTemplateVariables = (text: string, formData: Record<string, string>): string => {
  let processedText = text;
  
  // Find all @question_id patterns in the text
  const templateMatches = text.match(/@[a-zA-Z_][a-zA-Z0-9_]*/g);
  
  if (templateMatches) {
    templateMatches.forEach(match => {
      // Remove the @ symbol to get the question_id
      const questionId = match.substring(1);
      
      // Replace with the user's response if it exists
      if (formData[questionId]) {
        processedText = processedText.replace(new RegExp(match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), formData[questionId]);
      }
    });
  }
  
  return processedText;
};

export class PDFReportService {
  private doc: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private currentY: number;
  private logoCache: Record<string, string> = {}; // Cache for base64 logos
  private colors = {
    primary: '#B4D670',
    primaryDark: '#9cc24a',
    primaryLight: '#f0f8e8',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b',
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827'
    }
  };

  // Platform logo base64 strings - loaded at runtime
  private platformLogos: Record<string, string> = {
    openai: '',
    claude: '',
    anthropic: '',
    gemini: '',
    grok: '',
    perplexity: '',
  };

  constructor() {
    this.doc = new jsPDF('p', 'pt', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 40;
    this.currentY = this.margin;
  }

  // Load logos from public folder and convert to base64
  private async loadPlatformLogos(): Promise<void> {
    const logoFiles: Record<string, string> = {
      openai: '/OpenAI.svg',
      claude: '/Claude.svg',
      anthropic: '/Claude.svg', // Using Claude logo for Anthropic
      gemini: '/Gemini.svg',
      grok: '/Grok.svg',
      perplexity: '/Perplexity.svg',
    };

    const loadPromises = Object.entries(logoFiles).map(async ([platform, logoPath]) => {
      try {
        const base64 = await this.svgToBase64Image(logoPath);
        this.platformLogos[platform] = base64;
        console.log(`Successfully loaded logo for ${platform}`);
      } catch (error) {
        console.warn(`Failed to load logo for ${platform}:`, error);
        this.platformLogos[platform] = ''; // Keep empty on error
      }
    });

    await Promise.all(loadPromises);
    console.log('All logos loaded:', Object.keys(this.platformLogos).filter(k => this.platformLogos[k]));
  }

  // Convert SVG to PNG base64 for PDF compatibility
  private async svgToBase64Image(svgUrl: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        // Fetch the SVG content
        const response = await fetch(svgUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG: ${response.statusText}`);
        }
        const svgText = await response.text();
        
        // Create an image element
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        img.onload = () => {
          // Set canvas size (use fixed size for consistency)
          const size = 200; // Higher resolution for better quality
          canvas.width = size;
          canvas.height = size;
          
          // Fill with transparent background
          ctx.clearRect(0, 0, size, size);
          
          // Draw the image centered and scaled to fit
          const scale = Math.min(size / img.width, size / img.height);
          const x = (size - img.width * scale) / 2;
          const y = (size - img.height * scale) / 2;
          
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          
          // Convert to base64 PNG
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        };

        img.onerror = (error) => {
          reject(new Error(`Failed to load SVG image: ${error}`));
        };

        // Create blob URL from SVG text
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        img.src = url;
        
        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        reject(error);
      }
    });
  }

  public async generateReport(data: ReportData): Promise<void> {
    try {
      // Load platform logos first
      await this.loadPlatformLogos();
      
      // Sort platforms in consistent order to match web display
      const sortedData = {
        ...data,
        platformResults: this.sortPlatformResults(data.platformResults)
      };
      
      this.addModernHeader(sortedData);
      this.addMainContentGrid(sortedData);
      this.addAnalysisSummary(sortedData);
      this.addHeaders(sortedData);
      this.addFooter();
      
      // Download the PDF
      const fileName = `ai-visibility-report-${data.businessInfo.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      this.doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  // Sort platforms in a consistent order
  private sortPlatformResults(platforms: PlatformResult[]): PlatformResult[] {
    const platformOrder = ['openai', 'chatgpt', 'gemini', 'google', 'grok', 'x', 'perplexity', 'claude', 'anthropic'];
    
    return [...platforms].sort((a, b) => {
      const normalizedA = this.normalizePlatformName(a.platform);
      const normalizedB = this.normalizePlatformName(b.platform);
      
      const indexA = platformOrder.indexOf(normalizedA);
      const indexB = platformOrder.indexOf(normalizedB);
      
      // If both found in order array, sort by position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only A found, A comes first
      if (indexA !== -1) return -1;
      
      // If only B found, B comes first
      if (indexB !== -1) return 1;
      
      // If neither found, sort alphabetically
      return a.platform.localeCompare(b.platform);
    });
  }

  private addModernHeader(data: ReportData): void {
    // White background header container
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(0, 0, this.pageWidth, 200, 'F');
    
    // Green border at top to match the web design
    this.doc.setFillColor(180, 214, 112);
    this.doc.rect(0, 0, this.pageWidth, 4, 'F');

    // Status badge - matching the web design
    const badgeText = data.isComplete ? 'Analysis Complete' : 'Analysis Running...';
    this.doc.setFillColor(180, 214, 112);
    const badgeWidth = 120;
    const badgeX = (this.pageWidth - badgeWidth) / 2;
    this.doc.roundedRect(badgeX, 30, badgeWidth, 20, 10, 10, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(badgeText, badgeX + badgeWidth/2, 42, { align: 'center' });

    // Main title - matching the large title on web
    this.doc.setTextColor(31, 41, 55); // gray-800
    this.doc.setFontSize(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Your AI Search Visibility Report', this.pageWidth/2, 80, { align: 'center' });
    
    // Business summary - matching the web design
    if (data.businessInfo) {
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(75, 85, 99); // gray-600
      
      let summaryText = `Here's how ${data.businessInfo.name}`;
      if (data.formData.service) {
        summaryText += ` (${data.formData.service})`;
      }
      summaryText += ` appears in AI results for ${data.formData.city}`;
      
      this.doc.text(summaryText, this.pageWidth/2, 105, { align: 'center', maxWidth: this.pageWidth - 80 });
    }

    // Generated date
    this.doc.setFontSize(10);
    this.doc.setTextColor(107, 114, 128);
    this.doc.text(`Generated on ${new Date().toLocaleDateString()}`, this.pageWidth/2, 130, { align: 'center' });
    
    // Add separator line like the web design
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    this.doc.line(this.margin, 160, this.pageWidth - this.margin, 160);
    
    this.currentY = 180;
  }

  private addMainContentGrid(data: ReportData): void {
    // Create a two-column layout similar to the web design
    const leftColumnX = this.margin;
    const leftColumnWidth = 200;
    const rightColumnX = leftColumnX + leftColumnWidth + 40;
    const rightColumnWidth = this.pageWidth - rightColumnX - this.margin;
    
    // Left Column - Score Section (matches web design)
    this.addScoreCard(leftColumnX, leftColumnWidth, data);
    
    // Right Column - Platform Breakdown (matches web design)
    this.addPlatformBreakdownCard(rightColumnX, rightColumnWidth, data);
  }

  private addScoreCard(x: number, width: number, data: ReportData): void {
    const cardY = this.currentY;
    const cardHeight = 280;
    
    // Card background with border (matching web design)
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    this.doc.roundedRect(x, cardY, width, cardHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Overall Score', x + 20, cardY + 35);
    
    if (data.overallScore !== null) {
      // Score circle (simplified but styled like web version)
      const circleX = x + width/2;
      const circleY = cardY + 120;
      const radius = 45;
      
      // Background circle
      this.doc.setFillColor(229, 231, 235);
      this.doc.circle(circleX, circleY, radius, 'F');
      
      // Progress circle (simplified)
      this.doc.setFillColor(180, 214, 112);
      const progressRadius = radius - 5;
      const progressAngle = (data.overallScore / 10) * 360;
      
      // Create progress effect with multiple small circles
      for (let angle = 0; angle < progressAngle; angle += 8) {
        const radian = (angle - 90) * Math.PI / 180;
        const circlePointX = circleX + progressRadius * Math.cos(radian);
        const circlePointY = circleY + progressRadius * Math.sin(radian);
        this.doc.circle(circlePointX, circlePointY, 3, 'F');
      }
      
      // Inner white circle
      this.doc.setFillColor(255, 255, 255);
      this.doc.circle(circleX, circleY, radius - 8, 'F');
      
      // Score number
      this.doc.setTextColor(31, 41, 55);
      this.doc.setFontSize(24);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(data.overallScore.toString(), circleX, circleY - 5, { align: 'center' });
      
      // "out of 10" text
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(107, 114, 128);
      this.doc.text('out of 10', circleX, circleY + 8, { align: 'center' });
      
      // Score description
      this.doc.setFontSize(10);
      this.doc.setTextColor(107, 114, 128);
      const description = this.getScoreDescription(data.overallScore);
      this.doc.text(description, circleX, circleY + 65, { 
        align: 'center', 
        maxWidth: width - 20 
      });
    } else {
      // Loading state
      this.doc.setTextColor(107, 114, 128);
      this.doc.setFontSize(12);
      this.doc.text('Calculating...', x + width/2, cardY + 120, { align: 'center' });
    }
  }

  private addPlatformBreakdownCard(x: number, width: number, data: ReportData): void {
    const cardY = this.currentY;
    let cardHeight = Math.max(280, 60 + data.platformResults.length * 50);
    
    // Card background with border
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    this.doc.roundedRect(x, cardY, width, cardHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Platform Breakdown', x + 20, cardY + 35);
    
    let itemY = cardY + 60;
    
    data.platformResults.forEach((result) => {
      // Platform item background (hover effect simulation)
      this.doc.setFillColor(249, 250, 251);
      this.doc.roundedRect(x + 10, itemY, width - 20, 40, 4, 4, 'F');
      
      // Add platform logo with proper square dimensions
      this.addPlatformLogo(result.platform, x + 20, itemY + 5, 30, 30);
    
      
      // Status with color
      const status = this.getPlatformStatus(result);
      const statusColor = this.getPlatformStatusColorRGB(result);
      this.doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(status, x + 200, itemY + 20); // Adjusted position
      
      // Query results
      if (result.foundCount !== undefined && result.totalQueries !== undefined) {
        this.doc.setTextColor(107, 114, 128);
        this.doc.setFont('helvetica', 'normal');
        this.doc.text(`${result.foundCount}/${result.totalQueries} queries found`, x + 60, itemY + 20);
      }
      
      itemY += 45;
    });
    
    // Update currentY for next section
    this.currentY = cardY + cardHeight + 30;
  }

  private addDetailedResults(data: ReportData): void {
    this.checkPageBreak(100);
    
    // Section title matching web design
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 40, 8, 8, 'FD');
    
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Detailed Query Results', this.margin + 20, this.currentY + 25);
    this.currentY += 60;
    
    data.platformResults.forEach((platformResult) => {
      if (!platformResult.queries || platformResult.queries.length === 0) return;
      
      const platformName = this.getPlatformDisplayName(platformResult.platform);
      
      platformResult.queries.forEach((queryResult, index) => {
        // Start each query card on a new page
        this.doc.addPage();
        this.addPageHeader(data); // Add header to the new page
        this.currentY = 80; // Start content below header
        
        // Platform header with logo at the top of each page (square aspect ratio)
        this.addPlatformLogo(platformResult.platform, this.margin, this.currentY, 30, 30);
        
        // Move currentY down to start card after the logo
        this.currentY += 35; // 30px logo height + 5px spacing
        
        // Calculate dynamic height based on content
        const processedQuery = replaceTemplateVariables(queryResult.query.query_text, data.formData);
        const responseText = queryResult.result?.raw_response || queryResult.error || 'No response available';
        
        const maxWidth = this.pageWidth - 2 * this.margin - 40;
        
        // Calculate text heights
        this.doc.setFontSize(9);
        const queryLines = this.doc.splitTextToSize(`Query: ${processedQuery}`, maxWidth);
        const responseLines = this.doc.splitTextToSize(
          queryResult.result?.raw_response ? `Response: ${queryResult.result.raw_response}` :
          queryResult.error ? `Error: ${queryResult.error}` : 'No response available',
          maxWidth
        );
        
        // Calculate required height
        const titleHeight = 20;
        const queryHeight = queryLines.length * 12;
        const responseHeight = responseLines.length * 12;
        const metadataHeight = 20; // For confidence and rank
        const padding = 40; // Top and bottom padding
        
        const cardHeight = Math.max(100, titleHeight + queryHeight + responseHeight + metadataHeight + padding);
        
        // Query card background with dynamic height
        const borderColor = queryResult.found ? [16, 185, 129] : [239, 68, 68];
        const bgColor = queryResult.found ? [240, 253, 244] : [254, 242, 242];
        
        this.doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        this.doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        this.doc.setLineWidth(1);
        this.doc.roundedRect(this.margin + 10, this.currentY, this.pageWidth - 2 * this.margin - 20, cardHeight, 6, 6, 'FD');
        
        // Query title with status badge
        this.doc.setFontSize(12);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setTextColor(31, 41, 55);
        this.doc.text(`${index + 1}. ${queryResult.query.query_title}`, this.margin + 20, this.currentY + 20);
        
        // Status badge
        const statusText = queryResult.found ? 'Found' : 'Not Found';
        const statusBgColor = queryResult.found ? [16, 185, 129] : [239, 68, 68];
        
        this.doc.setFillColor(statusBgColor[0], statusBgColor[1], statusBgColor[2]);
        this.doc.roundedRect(this.pageWidth - 80, this.currentY + 8, 50, 16, 8, 8, 'F');
        this.doc.setTextColor(255, 255, 255);
        this.doc.setFontSize(8);
        this.doc.setFont('helvetica', 'bold');
        this.doc.text(statusText, this.pageWidth - 55, this.currentY + 18, { align: 'center' });
        
        // Processed query text with proper line wrapping
        this.doc.setFontSize(9);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setTextColor(75, 85, 99);
        
        let textY = this.currentY + 40;
        queryLines.forEach((line: string) => {
          this.doc.text(line, this.margin + 20, textY);
          textY += 12;
        });
        
        // Add spacing between query and response
        textY += 8;
        
        // Response or error with proper line wrapping
        if (queryResult.result?.raw_response) {
          this.doc.setTextColor(75, 85, 99);
          responseLines.forEach((line: string) => {
            this.doc.text(line, this.margin + 20, textY);
            textY += 12;
          });
        } else if (queryResult.error) {
          this.doc.setTextColor(220, 38, 38);
          responseLines.forEach((line: string) => {
            this.doc.text(line, this.margin + 20, textY);
            textY += 12;
          });
        } else {
          this.doc.setTextColor(107, 114, 128);
          this.doc.text('No response available', this.margin + 20, textY);
          textY += 12;
        }
        
        // Confidence and rank at the bottom
        const metadataY = this.currentY + cardHeight - 20;
        if (queryResult.confidence) {
          this.doc.setTextColor(107, 114, 128);
          this.doc.setFontSize(8);
          this.doc.text(`Confidence: ${Math.round(queryResult.confidence * 100)}%`, this.margin + 20, metadataY);
        }
        
        if (queryResult.rank) {
          this.doc.text(`Rank: #${queryResult.rank}`, this.margin + 120, metadataY);
        }
        
        // Update currentY to after the card for proper positioning if more content is added
        this.currentY += cardHeight + 15;
      });
    });
  }

  private addAnalysisSummary(data: ReportData): void {
    this.checkPageBreak(200);
    
    // Summary section with card design
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    const summaryHeight = 150;
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, summaryHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Analysis Summary', this.margin + 20, this.currentY + 30);
    
    // Summary content
    const totalQueries = data.platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0);
    const successfulFinds = data.platformResults.reduce((sum, result) => sum + (result.foundCount || 0), 0);
    const foundPercentage = totalQueries > 0 ? Math.round((successfulFinds / totalQueries) * 100) : 0;
    
    const summaryText = [
      `${data.businessInfo.name} currently has ${this.getVisibilityLevel(data.overallScore)} visibility in AI-driven search results,`,
      `with an overall score of ${data.overallScore}/10. The analysis covered ${totalQueries} search queries across`,
      `${data.platformResults.length} AI platforms, with the business found in ${foundPercentage}% of search queries.`
    ].join(' ');
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(75, 85, 99);
    this.doc.text(summaryText, this.margin + 20, this.currentY + 55, {
      maxWidth: this.pageWidth - 2 * this.margin - 40,
      lineHeightFactor: 1.4
    });
    
    // Statistics row (matching web design)
    const statsY = this.currentY + 100;
    const statWidth = (this.pageWidth - 2 * this.margin - 60) / 3;
    
    // Total Queries stat
    this.addStatBox(this.margin + 20, statsY, statWidth, totalQueries.toString(), 'Total Queries Run');
    
    // Success Rate stat  
    this.addStatBox(this.margin + 20 + statWidth + 20, statsY, statWidth, `${foundPercentage}%`, 'Business Found Rate');
    
    // Average Position stat
    const allRanks = data.platformResults
      .filter(r => r.queries)
      .flatMap(r => r.queries!)
      .filter(q => q.found && q.rank)
      .map(q => q.rank!);
    const avgRank = allRanks.length > 0 ? (allRanks.reduce((sum, rank) => sum + rank, 0) / allRanks.length).toFixed(1) : 'N/A';
    
    this.addStatBox(this.margin + 20 + 2 * (statWidth + 20), statsY, statWidth, `#${avgRank}`, 'Avg. Position When Found');
    
    this.currentY += summaryHeight + 40;
    
    // Add Query Statistics section
    this.addQueryStatistics(data);
    
    // Add Query Type Summary section
    this.addQueryTypeSummary(data);
    
    // Add Recommendations section
    this.addRecommendations(data);
    
    // Add detailed results after summary
    this.addDetailedResults(data);
  }

  private addRecommendations(data: ReportData): void {
    this.checkPageBreak(200);
    
    const recommendations = this.generateRecommendations(data);
    
    if (recommendations.length === 0) {
      return;
    }
    
    // Calculate required height
    let estimatedHeight = 80; // Header + padding
    recommendations.forEach(rec => {
      // Estimate text height for each recommendation
      const titleLines = Math.ceil(rec.title.length / 60);
      const descLines = Math.ceil(rec.description.length / 80);
      estimatedHeight += (titleLines * 14) + (descLines * 12) + 35;
    });
    
    this.checkPageBreak(estimatedHeight);
    
    // Recommendations section matching web design
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, estimatedHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Recommendations for Improvement', this.margin + 20, this.currentY + 30);
    
    let itemY = this.currentY + 55;
    
    recommendations.forEach((recommendation, index) => {
      // Recommendation item background
      this.doc.setFillColor(249, 250, 251);
      
      // Calculate item height based on content
      const maxWidth = this.pageWidth - 2 * this.margin - 80;
      const titleLines = this.doc.splitTextToSize(recommendation.title, maxWidth);
      const descLines = this.doc.splitTextToSize(recommendation.description, maxWidth);
      const itemHeight = Math.max(60, (titleLines.length * 14) + (descLines.length * 12) + 25);
      
      this.doc.roundedRect(this.margin + 20, itemY, this.pageWidth - 2 * this.margin - 40, itemHeight, 6, 6, 'F');
      
      // Recommendation number
      this.doc.setFillColor(180, 214, 112);
      this.doc.circle(this.margin + 40, itemY + 20, 12, 'F');
      
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text((index + 1).toString(), this.margin + 40, itemY + 24, { align: 'center' });
      
      // Recommendation title
      this.doc.setTextColor(31, 41, 55);
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'bold');
      
      let textY = itemY + 18;
      titleLines.forEach((line: string) => {
        this.doc.text(line, this.margin + 60, textY);
        textY += 14;
      });
      
      // Recommendation description
      textY += 5;
      this.doc.setTextColor(75, 85, 99);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      
      descLines.forEach((line: string) => {
        this.doc.text(line, this.margin + 60, textY);
        textY += 12;
      });
      
      itemY += itemHeight + 15;
    });
    
    this.currentY = itemY + 15;
  }

  private generateRecommendations(data: ReportData): Array<{title: string, description: string}> {
    const recommendations = [];
    const score = data.overallScore;
    const successfulPlatforms = data.platformResults.filter(p => p.found).length;
    const totalPlatforms = data.platformResults.length;
    
    if (score < 5) {
      recommendations.push({
        title: 'Improve Online Presence',
        description: 'Your business has limited visibility across AI platforms. Focus on creating high-quality, keyword-optimized content that clearly describes your services and location. Ensure your business information is consistent across all online directories and platforms.'
      });
    }
    
    if (successfulPlatforms < totalPlatforms / 2) {
      recommendations.push({
        title: 'Diversify AI Platform Optimization',
        description: 'Your business appears on less than half of the tested AI platforms. Consider creating content specifically optimized for different AI search engines, as each platform may have different ranking criteria and data sources.'
      });
    }
    
    recommendations.push({
      title: 'Monitor and Track Progress',
      description: 'Regular monitoring of your AI search visibility is crucial. Set up monthly assessments to track improvements and identify emerging opportunities across different AI platforms.'
    });
    
    if (data.formData.website || data.formData.company_website || data.formData.business_website) {
      recommendations.push({
        title: 'Optimize Website Content',
        description: 'Ensure your website content clearly states your services, location, and contact information. Use structured data markup to help AI systems better understand and categorize your business information.'
      });
    }
    
    recommendations.push({
      title: 'Enhance Local SEO',
      description: 'Focus on local search optimization by claiming and optimizing your Google My Business profile, gathering customer reviews, and ensuring NAP (Name, Address, Phone) consistency across all online platforms.'
    });
    
    return recommendations;
  }

  private addQueryStatistics(data: ReportData): void {
    this.checkPageBreak(150);
    
    // Query Statistics section matching web design
    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    const statsHeight = 120;
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, statsHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Search Query Statistics', this.margin + 20, this.currentY + 30);
    
    // Statistics grid
    const statsY = this.currentY + 50;
    const statWidth = (this.pageWidth - 2 * this.margin - 80) / 3;
    
    const totalQueries = data.platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0);
    const successfulFinds = data.platformResults.reduce((sum, result) => sum + (result.foundCount || 0), 0);
    const foundPercentage = totalQueries > 0 ? Math.round((successfulFinds / totalQueries) * 100) : 0;
    
    // Total Queries stat
    this.addDetailedStatBox(this.margin + 20, statsY, statWidth, totalQueries.toString(), `out of ${totalQueries}`, 'Total Queries Run');
    
    // Success Rate stat
    this.addDetailedStatBox(this.margin + 20 + statWidth + 20, statsY, statWidth, `${foundPercentage}%`, `${successfulFinds} successful mentions`, 'Business Found Rate');
    
    // Average Position stat
    const allRanks = data.platformResults
      .filter(r => r.queries)
      .flatMap(r => r.queries!)
      .filter(q => q.found && q.rank)
      .map(q => q.rank!);
    const avgRank = allRanks.length > 0 ? (allRanks.reduce((sum, rank) => sum + rank, 0) / allRanks.length).toFixed(1) : 'N/A';
    
    this.addDetailedStatBox(this.margin + 20 + 2 * (statWidth + 20), statsY, statWidth, `#${avgRank}`, 'Lower numbers are better', 'Avg. Position When Found');
    
    this.currentY += statsHeight + 30;
  }

  private addDetailedStatBox(x: number, y: number, width: number, number: string, subtitle: string, label: string): void {
    // Stat box background matching web design
    this.doc.setFillColor(249, 250, 251);
    this.doc.roundedRect(x, y, width, 50, 4, 4, 'F');
    
    // Number
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(number, x + width/2, y + 18, { align: 'center' });
    
    // Subtitle
    this.doc.setTextColor(107, 114, 128);
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(subtitle, x + width/2, y + 30, { align: 'center' });
    
    // Label
    this.doc.setTextColor(75, 85, 99);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(label, x + width/2, y + 43, { align: 'center' });
  }

  private addQueryTypeSummary(data: ReportData): void {
    this.checkPageBreak(200);
    
    // Calculate query type statistics
    const queryTypeStats = this.calculateQueryTypeStats(data);
    const queryTypes = Object.entries(queryTypeStats);
    
    if (queryTypes.length === 0) {
      return;
    }
    
    // Query Type Summary section matching web design
    this.doc.setFillColor(249, 250, 251);
    this.doc.setDrawColor(229, 231, 235);
    this.doc.setLineWidth(1);
    const summaryHeight = 60 + queryTypes.length * 25;
    this.doc.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, summaryHeight, 8, 8, 'FD');
    
    // Section title
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Query Type Summary', this.margin + 20, this.currentY + 30);
    
    let itemY = this.currentY + 50;
    
    queryTypes.forEach(([queryType, stats]) => {
      const successRate = stats.total > 0 ? Math.round((stats.found / stats.total) * 100) : 0;
      
      // Query type row
      const rowX = this.margin + 20;
      const rowWidth = this.pageWidth - 2 * this.margin - 40;
      
      // Query type name and stats
      this.doc.setTextColor(55, 65, 81);
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(`${queryType}:`, rowX, itemY);
      
      this.doc.setTextColor(107, 114, 128);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`${stats.found} found`, rowX + 120, itemY);
      
      // Progress bar
      const progressBarX = rowX + 200;
      const progressBarWidth = 100;
      const progressBarHeight = 6;
      
      // Background bar
      this.doc.setFillColor(229, 231, 235);
      this.doc.roundedRect(progressBarX, itemY - 3, progressBarWidth, progressBarHeight, 2, 2, 'F');
      
      // Progress fill
      if (successRate > 0) {
        this.doc.setFillColor(239, 68, 68); // Red color matching web design
        const fillWidth = (progressBarWidth * successRate) / 100;
        this.doc.roundedRect(progressBarX, itemY - 3, fillWidth, progressBarHeight, 2, 2, 'F');
      }
      
      // Success rate text
      this.doc.setTextColor(107, 114, 128);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(`${successRate}% success rate`, progressBarX + progressBarWidth + 10, itemY);
      
      itemY += 25;
    });
    
    this.currentY += summaryHeight + 30;
  }

  private calculateQueryTypeStats(data: ReportData): Record<string, { total: number; found: number }> {
    const queryTypeStats: Record<string, { total: number; found: number }> = {};
    
    // Get all unique queries from platform results
    const allQueries = new Map<string, any>();
    
    data.platformResults.forEach(platformResult => {
      if (platformResult.queries) {
        platformResult.queries.forEach(queryResult => {
          allQueries.set(queryResult.query.query_id, queryResult.query);
        });
      }
    });
    
    // Calculate stats for each query type
    allQueries.forEach(query => {
      const queryType = query.query_title || 'Unknown';
      if (!queryTypeStats[queryType]) {
        queryTypeStats[queryType] = { total: 0, found: 0 };
      }
      
      // Count total occurrences across platforms
      data.platformResults.forEach(platformResult => {
        if (platformResult.queries) {
          const queryResult = platformResult.queries.find(qr => qr.query.query_id === query.query_id);
          if (queryResult) {
            queryTypeStats[queryType].total += 1;
            if (queryResult.found) {
              queryTypeStats[queryType].found += 1;
            }
          }
        }
      });
    });
    
    return queryTypeStats;
  }

  private addStatBox(x: number, y: number, width: number, number: string, label: string): void {
    // Stat box background
    this.doc.setFillColor(249, 250, 251);
    this.doc.roundedRect(x, y, width, 35, 4, 4, 'F');
    
    // Number
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(number, x + width/2, y + 15, { align: 'center' });
    
    // Label
    this.doc.setTextColor(107, 114, 128);
    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(label, x + width/2, y + 28, { align: 'center' });
  }

  private addFooter(): void {
    const pageCount = (this.doc as any).internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      
      // Footer background
      this.doc.setFillColor(249, 250, 251);
      this.doc.rect(0, this.pageHeight - 30, this.pageWidth, 30, 'F');
      
      // Footer text
      this.doc.setFontSize(8);
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(
        `Page ${i} of ${pageCount} • AI Visibility Report • Generated ${new Date().toLocaleDateString()}`,
        this.pageWidth/2,
        this.pageHeight - 15,
        { align: 'center' }
      );
    }
  }

  private addHeaders(data: ReportData): void {
    const pageCount = (this.doc as any).internal.getNumberOfPages();
    
    for (let i = 2; i <= pageCount; i++) { // Start from page 2, skip the first page
      this.doc.setPage(i);
      this.addPageHeader(data);
    }
  }

  private addPageHeader(data: ReportData): void {
    // Header background - exact match to web header (#2a2a2a)
    this.doc.setFillColor(42, 42, 42); // #2a2a2a
    this.doc.rect(0, 0, this.pageWidth, 50, 'F');
    
    // Bottom border like web header (#333)
    this.doc.setDrawColor(51, 51, 51); // #333
    this.doc.setLineWidth(1);
    this.doc.line(0, 50, this.pageWidth, 50);
    
    // Logo section - matching web header layout (logo + text side by side)
    const logoX = this.margin; // padding: 1rem like web
    const logoY = 10; // Center vertically in 50px header
    
    
    // "AI Visibility Platform" text - exact match to web header
    this.doc.setTextColor(255, 255, 255); // White text like web
    this.doc.setFontSize(18); // font-size: 1.5rem and font-weight: 700
    this.doc.setFont('helvetica', 'bold');
    
    // Position after logo with gap: 0.75rem like web header
    const textX = logoX; // 40px logo width + 10px gap (0.75rem)
    const textY = 35; // Center vertically
    
    this.doc.text('AI Visibility Platform', textX, textY);
  }

  // Helper methods
  private checkPageBreak(requiredSpace: number): void {
    if (this.currentY + requiredSpace > this.pageHeight - 50) {
      this.doc.addPage();
      this.addPageHeader({ businessInfo: { name: '', domain: '' }, formData: {}, platformResults: [], overallScore: 0, isComplete: false } as ReportData);
      this.currentY = 80; // Start content below header
    }
  }

  private getVisibilityLevel(score: number): string {
    if (score >= 8) return 'excellent';
    if (score >= 6) return 'good';
    if (score >= 4) return 'moderate';
    if (score >= 2) return 'limited';
    return 'poor';
  }

  private getScoreDescription(score: number): string {
    if (score >= 8) return 'Excellent visibility in AI searches';
    if (score >= 6) return 'Good visibility in AI searches';
    if (score >= 4) return 'Moderate visibility in AI searches';
    if (score >= 2) return 'Limited visibility in AI searches';
    return 'Poor visibility in AI searches';
  }

  private getPlatformStatus(result: PlatformResult): string {
    if (result.status === 'error') return 'Error';
    if (result.status === 'running') return 'Running...';
    if (result.status === 'pending') return 'Pending';
    if (result.status === 'completed') {
      if (result.foundCount && result.foundCount > 0) {
        return result.foundCount > (result.totalQueries! / 2) ? 'Great' : 'Good';
      }
      return 'Not Found';
    }
    return 'Unknown';
  }

  private getPlatformStatusColorRGB(result: PlatformResult): [number, number, number] {
    if (result.status === 'error' || (result.status === 'completed' && !result.found)) return [239, 68, 68];
    if (result.status !== 'completed') return [107, 114, 128];
    if (result.foundCount && result.foundCount > (result.totalQueries! / 2)) return [16, 185, 129];
    if (result.foundCount && result.foundCount > 0) return [245, 158, 11];
    return [239, 68, 68];
  }

  private getPlatformDisplayName(platform: string): string {
    const normalizedPlatform = this.normalizePlatformName(platform);
    const names: Record<string, string> = {
      openai: 'ChatGPT',
      claude: 'Claude',
      anthropic: 'Claude',
      gemini: 'Gemini',
      grok: 'Grok',
      perplexity: 'Perplexity'
    };
    return names[normalizedPlatform] || platform;
  }

  // Method to load logos from URLs and convert to base64
  private async loadLogoAsBase64(logoUrl: string): Promise<string> {
    try {
      const response = await fetch(logoUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch logo: ${response.statusText}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Failed to load logo:', logoUrl, error);
      return '';
    }
  }

  // Normalize platform name to match logo keys
  private normalizePlatformName(platform: string): string {
    const normalizedMap: Record<string, string> = {
      'chatgpt': 'openai',
      'openai': 'openai',
      'claude': 'claude',
      'anthropic': 'claude',
      'gemini': 'gemini',
      'google': 'gemini',
      'grok': 'grok',
      'x': 'grok',
      'perplexity': 'perplexity'
    };
    
    return normalizedMap[platform.toLowerCase()] || platform.toLowerCase();
  }

  // Method to add platform logo to PDF
  private addPlatformLogo(platform: string, x: number, y: number, width: number = 20, height: number = 20): void {
    try {
      const normalizedPlatform = this.normalizePlatformName(platform);
      const logoBase64 = this.platformLogos[normalizedPlatform];
      
      console.log(`Adding logo for ${platform} (normalized: ${normalizedPlatform}), has logo: ${!!logoBase64}`);
      
      if (logoBase64) {
        // Use PNG format since we convert SVGs to PNG
        this.doc.addImage(logoBase64, 'PNG', x, y, width, height);
      } else {
        // Fallback to styled text/icon
        console.log(`Using fallback for ${platform}`);
        this.addPlatformFallback(platform, x, y, width, height);
      }
    } catch (error) {
      console.warn('Error adding platform logo:', platform, error);
      this.addPlatformFallback(platform, x, y, width, height);
    }
  }

  // Fallback method for platforms without logos
  private addPlatformFallback(platform: string, x: number, y: number, width: number, height: number): void {
    const colors = this.getPlatformColors(platform);
    
    // Create colored background
    this.doc.setFillColor(colors.bg[0], colors.bg[1], colors.bg[2]);
    this.doc.roundedRect(x, y, width, height, 3, 3, 'F');
    
    // Add platform initial or icon
    this.doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    
    const initial = platform.charAt(0).toUpperCase();
    this.doc.text(initial, x + width/2, y + height/2 + 3, { align: 'center' });
  }

  // Get platform-specific colors
  private getPlatformColors(platform: string): { bg: [number, number, number], text: [number, number, number] } {
    const normalizedPlatform = this.normalizePlatformName(platform);
    const colorMap: Record<string, { bg: [number, number, number], text: [number, number, number] }> = {
      openai: { bg: [116, 185, 122], text: [255, 255, 255] }, // Green
      claude: { bg: [217, 119, 6], text: [255, 255, 255] }, // Orange
      anthropic: { bg: [217, 119, 6], text: [255, 255, 255] }, // Orange
      gemini: { bg: [59, 130, 246], text: [255, 255, 255] }, // Blue
      grok: { bg: [0, 0, 0], text: [255, 255, 255] }, // Black
      perplexity: { bg: [99, 102, 241], text: [255, 255, 255] }, // Purple
    };
    
    return colorMap[normalizedPlatform] || { bg: [107, 114, 128], text: [255, 255, 255] };
  }
}

export const pdfReportService = new PDFReportService();